import React, { useContext, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { reaction, when } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import { Helmet } from 'react-helmet';

import { withErrorBoundary } from 'react-error-boundary';

import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import Filters from 'components/Catalog/Filters';
import { storesContext } from 'stores';
import {
  CatalogLayout,
  CenteredLoadingSpinner,
  useFeature,
} from '@ole-ui/ole-ui-components';
import './styles.scss';

import { CUSTOM_SKILL_PATH_MODE } from 'config/constants';
import { EnablementFeatureFlags } from 'config/featureFlags';
import CustomSkillPathOfferingsList from 'components/LearningPath/CustomSkillForm/OfferingList';
import CustomSkillPathForm from 'components/LearningPath/CustomSkillForm';
import ManageUsersInPath from 'components/LearningPath/CustomSkillForm/ManageUsersInPath';
import OrgLeaderCustomPathSelection from 'components/LearningPath/CustomSkillForm/OrgLeaderCustomPathSelection';
import { Button, Pagination, PaginationVariant } from '@patternfly/react-core';
import SkillsPathForbiddenView from 'views/SkillsPath/ForbiddenView';
import CustomSkillPathStore, { customSkillPathContext } from './store';
import catalogPaginationPerPageOptions from '../Catalog/catalogPaginationOptions';

// Declare the window object
declare const window: Window & typeof globalThis;

const CustomSkillPath = () => {
  const { i18n, t } = useTranslation();
  const isSkillsPathsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    defaultFlagValue: false,
  });
  const {
    catalogStore,
    featuresStore,
    learningPathStore,
    routerStore,
    searchStore,
    userStore,
    vocabularyStore,
    organizationStore,
  } = useContext(storesContext);

  const listRef = React.useRef<HTMLHeadingElement>(null);

  const store = useLocalStore(
    () =>
      new CustomSkillPathStore(
        catalogStore,
        learningPathStore,
        userStore,
        organizationStore,
        routerStore,
        vocabularyStore,
      ),
  );

  const route = routerStore?.route as any;
  const pageTitle = route?.params?.title;

  const {
    currentPage,
    paginatedEntries,
    filteredCount,
    setCurrentPage,
    entriesPerPage,
  } = catalogStore;

  const updateCatalog = async () => {
    const entries = await catalogStore.getCurrentSubscriptionCatalog(true);

    if (entries) {
      catalogStore.getFilterInfo();
    }
  };

  const presetFilters = () => {
    catalogStore.clearFilters();
    if (route?.params?.q) {
      searchStore.query = route?.params?.q;
    } else {
      searchStore.query = '';
    }
    catalogStore.presetFilters();
  };

  useEffect(() => {
    // Populate entry data and filters on initial load
    when(
      () =>
        !catalogStore.loaded &&
        (userStore.subscription.subscription || userStore.isOrganizationLeader),
      async () => {
        catalogStore.getFilterInfo();
      },
    );

    // Select preset filters on initial load
    when(
      () => catalogStore.loaded && route?.name,
      async () => {
        await store.init(i18n, t);
        presetFilters();
      },
    );

    // Update preset filters when route changes
    reaction(
      () => catalogStore.loaded && route?.name,
      async () => {
        presetFilters();
      },
    );

    reaction(
      () => catalogStore.loaded && route?.params?.q,
      () => catalogStore.filterEntries(),
    );
    // when leaving this route - refresh catalog based on curret user subscription
    return () => {
      updateCatalog();
    };
  }, []);

  const renderAddUsersModal = () => (
    <ManageUsersInPath
      subscription={store.targetSubscription}
      usersToAdd={store.userListToAdd}
      usersInPath={store.targetUsersInPath}
      title={store.customSkillPathTitle}
      showModal={store.showAddUserModal}
      onCancelUsersToPath={() => {
        store.toggleAddUserToPathModal();
      }}
      onAddUsersToPath={(selectedUsers) => {
        store.setUsersInPath(selectedUsers);
        store.toggleAddUserToPathModal();
      }}
    />
  );

  const autoScroll = () => {
    (listRef.current as any)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handleCancel = () => {
    routerStore.navigate('skills-paths');
  };

  if (!featuresStore.allFeaturesFetched) {
    return (
      <CatalogLayout>
        <CatalogLayout.Content>
          <div className="learning-paths__loading-container">
            <CenteredLoadingSpinner />
          </div>
        </CatalogLayout.Content>
      </CatalogLayout>
    );
  }

  if (!isSkillsPathsEnabled && !userStore.isOrganizationLeader) {
    return <SkillsPathForbiddenView />;
  }

  return (
    <>
      <Helmet title={`${pageTitle} - Red Hat Learning`} />

      {store.allowToSelectCustomPathType && (
        <CatalogLayout>
          <CatalogLayout.Content>
            <OrgLeaderCustomPathSelection
              subscriptions={store.uniqueSubscriptionInUserListToAdd}
              isSubscriptionsLoading={store.loadingUserListToAdd}
              onSelectMyself={() => {
                store.allowToAddUsersToPath = false;
                store.allowFilters = true;
                store.setTargetSubscription(userStore.currentSubscriptionCode);
                store.setCustomSkillPathMode(CUSTOM_SKILL_PATH_MODE.create);
                (window as any).scrollTo?.(0, 0);
              }}
              onSelectMyTeam={async (subscriptionCode) => {
                store.allowFilters = true;
                store.allowToAddUsersToPath = true;
                store.setTargetSubscription(subscriptionCode);
                store.setCustomSkillPathMode(CUSTOM_SKILL_PATH_MODE.create);
                (window as any).scrollTo?.(0, 0);
              }}
            />
          </CatalogLayout.Content>
        </CatalogLayout>
      )}

      {store.allowFilters && (
        <CatalogLayout>
          <CatalogLayout.Filters count={filteredCount}>
            <Filters
              title={t('Create a Custom skills path')}
              onFilterSelect={autoScroll}
              onSearch={autoScroll}
            >
              <div className="custom-skillpath__cancel-menu">
                <Button type="button" variant="danger" onClick={handleCancel}>
                  {t('Cancel')}
                </Button>
              </div>
            </Filters>
          </CatalogLayout.Filters>
          <CatalogLayout.Content>
            <div className="custom-skillpath__form">
              <customSkillPathContext.Provider value={store}>
                <CustomSkillPathForm />
              </customSkillPathContext.Provider>
            </div>

            <h3 ref={listRef}>{t('Available Offerings')}</h3>
            <CustomSkillPathOfferingsList
              targetSubscription={store.targetSubscription}
              offerings={paginatedEntries}
              offeringsInPath={store.customSkillPath.offerings}
              onAddToPath={(offeringCode) => {
                store.addOfferingsToPath(offeringCode);
              }}
            />
            <Pagination
              className="catalog__pagination custom-skillpath__pagination"
              itemCount={filteredCount}
              perPage={entriesPerPage}
              page={currentPage}
              onSetPage={(e, n) => setCurrentPage(n)}
              onPerPageSelect={(e, n) => {
                catalogStore.entriesPerPage = n;
              }}
              perPageOptions={catalogPaginationPerPageOptions}
              widgetId="custom-skills-path-pagination"
              ouiaId="CustomSkillsPathPagination"
              aria-label={t('Pagination controls')}
              variant={PaginationVariant.bottom}
            />
            {renderAddUsersModal()}
          </CatalogLayout.Content>
        </CatalogLayout>
      )}
    </>
  );
};

export default withErrorBoundary(
  observer(CustomSkillPath),
  GenericViewError,
  handleError,
);
